import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateCycle } from '../../../api/hooks';
import type { ExamScope } from '../../../api/types';
import { Modal, Button, SortableTh } from '../../../components/ui';
import { apiErrorText } from '../../../lib/apiError';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';

export interface RemediateCandidate { student_id: number; name: string; pct_by_chapter: Record<number, number | null> }

/** Server-side cap on the total question count across the cycle's chapters. */
export const MAX_TOTAL_QUESTIONS = 200;
const GENERIC_ERROR = 'Could not create the cycle. Check the selection and try again.';

interface Props {
  isOpen: boolean; onClose: () => void;
  cohort: { grade_id: number; section_id: number | null; subject_id: number; scopes: ExamScope[]; source_exam_id?: number | null };
  chapters: Array<{ chapter_id: number; name: string }>;
  candidates: RemediateCandidate[];
  passPct: number;
}

export default function RemediateDialog({ isOpen, onClose, cohort, chapters, candidates, passPct }: Props) {
  const navigate = useNavigate();
  const create = useCreateCycle();
  const proposed = useMemo(() => candidates.filter((c) =>
    chapters.some((ch) => { const p = c.pct_by_chapter[ch.chapter_id]; return p != null && p < passPct; })),
    [candidates, chapters, passPct]);
  const [ticked, setTicked] = useState<number[]>(() => proposed.map((c) => c.student_id));
  const [shown, setShown] = useState<number[]>(() => proposed.map((c) => c.student_id));
  const [adding, setAdding] = useState(false);
  const [counts, setCounts] = useState<Record<number, number>>(() =>
    Object.fromEntries(chapters.map((c) => [c.chapter_id, 5])));
  const [error, setError] = useState<string | null>(null);
  const byId = useMemo(() => Object.fromEntries(candidates.map((c) => [c.student_id, c])), [candidates]);
  const toggle = (id: number) => setTicked((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));
  const addable = candidates.filter((c) => !shown.includes(c.student_id));
  const totalQuestions = chapters.reduce((n, c) => n + (counts[c.chapter_id] || 5), 0);
  const overCap = totalQuestions > MAX_TOTAL_QUESTIONS;

  const shownCandidates = useMemo(
    () => shown.map((id) => byId[id]).filter((c): c is RemediateCandidate => Boolean(c)),
    [shown, byId],
  );
  const remediateColumns = useMemo<SortColumn<RemediateCandidate>[]>(() => [
    { key: 'name', accessor: (c) => c.name },
    ...chapters.map((ch) => ({
      key: `ch-${ch.chapter_id}`,
      accessor: (c: RemediateCandidate) => c.pct_by_chapter[ch.chapter_id],
      type: 'number' as const,
    })),
  ], [chapters]);
  const { rows: sortedShown, sort, toggle: toggleSort } = useSortableTable(shownCandidates, remediateColumns, { key: 'name', dir: 'asc' });

  async function confirm() {
    setError(null);
    try {
      const res = await create.mutateAsync({
        source_exam_id: cohort.source_exam_id ?? null,
        grade_id: cohort.grade_id, section_id: cohort.section_id, subject_id: cohort.subject_id,
        scopes: cohort.scopes,
        target_chapters: chapters.map((c) => ({ chapter_id: c.chapter_id, count: counts[c.chapter_id] || 5 })),
        target_student_ids: [...ticked].sort((a, b) => a - b),
      });
      navigate(`/exams/generate?cycle=${res.cycle_id}`);
    } catch (err) {
      // Show what the server actually said (permission scope, question cap, …)
      // instead of a generic sentence the teacher cannot act on.
      setError(apiErrorText(err, GENERIC_ERROR));
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Remedial exam" size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={confirm} disabled={ticked.length === 0 || overCap || create.isPending} loading={create.isPending}>
            Generate remedial exam
          </Button>
        </div>
      }>
      <div className="space-y-4">
        <div>
          <h4 className="mb-2 font-display text-sm font-bold text-ink">Chapters</h4>
          {chapters.map((c) => (
            <div key={c.chapter_id} className="flex items-center gap-2 py-1 text-sm text-ink">
              <span className="flex-1">{c.name}</span>
              <input type="number" min={1} max={20} aria-label={`questions for ${c.name}`}
                     className="min-h-[36px] w-16 rounded-[12px] border border-line-strong bg-surface px-2 text-sm text-ink outline-none focus:border-ink focus:ring-4 focus:ring-accent/40"
                     value={counts[c.chapter_id]}
                     onChange={(e) => {
                       const n = Number(e.target.value);
                       setCounts({ ...counts, [c.chapter_id]: Number.isFinite(n) ? Math.min(20, Math.max(1, Math.trunc(n))) : 1 });
                     }} />
              <span className="text-xs text-muted">questions</span>
            </div>
          ))}
          {overCap && (
            <p className="mt-1 text-xs text-danger-ink">
              Total questions across chapters must be {MAX_TOTAL_QUESTIONS} or fewer — currently {totalQuestions}.
              Lower the per-chapter counts or pick fewer chapters.
            </p>
          )}
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="font-display text-sm font-bold text-ink">Students ({ticked.length} selected)</h4>
            <Button size="sm" variant="ghost" onClick={() => setAdding((a) => !a)}>Add student</Button>
          </div>
          {adding && (
            <div className="mb-2 max-h-32 overflow-y-auto rounded-[12px] border border-line p-2 text-sm">
              {addable.length === 0 ? <span className="text-xs text-muted">Everyone is listed.</span>
                : addable.map((c) => (
                  <button key={c.student_id} type="button" className="mb-1 mr-2 rounded-full bg-surface-2 px-2.5 py-1 text-xs font-semibold text-ink hover:bg-mist"
                          onClick={() => { setShown((s) => [...s, c.student_id]); setTicked((t) => [...t, c.student_id]); }}>
                    {c.name}
                  </button>
                ))}
            </div>
          )}
          <table className="min-w-full text-sm">
            <thead><tr className="text-left"><th></th>
              <SortableTh columnKey="name" sort={sort} onToggle={toggleSort} >Student</SortableTh>
              {chapters.map((c) => (
                <SortableTh key={c.chapter_id} columnKey={`ch-${c.chapter_id}`} sort={sort} onToggle={toggleSort}>
                  {c.name}
                </SortableTh>
              ))}
            </tr></thead>
            <tbody>
              {sortedShown.map((c) => {
                const id = c.student_id;
                return (
                  <tr key={id} className="border-t border-line">
                    <td className="py-1.5 pr-2"><input type="checkbox" className="h-4 w-4 accent-navy" aria-label={`${c.name} (#${c.student_id})`} checked={ticked.includes(id)} onChange={() => toggle(id)} /></td>
                    <td className="px-2.5 py-1.5 font-medium text-ink">{c.name}</td>
                    {chapters.map((ch) => {
                      const p = c.pct_by_chapter[ch.chapter_id];
                      return <td key={ch.chapter_id} className={`px-2.5 py-1.5 font-mono text-xs ${p != null && p < passPct ? 'font-semibold text-danger-ink' : 'text-ink'}`}>{p == null ? '—' : `${p.toFixed(0)}%`}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {shown.length === 0 && <p className="mt-2 text-xs text-muted">Nobody is below {passPct}% on these chapters. Use "Add student" to pick manually.</p>}
        </div>
        {error && <p className="text-xs text-danger-ink">{error}</p>}
      </div>
    </Modal>
  );
}
